/**
 * Copyright (c) 2023 Gitpod GmbH. All rights reserved.
 * Licensed under the GNU Affero General Public License (AGPL).
 * See License.AGPL.txt in the project root for license information.
 */

import React, { useCallback } from "react";
import { type HideableCellProps, TableHead } from "./Table";
import { Button } from "@/components/flexkit/Button";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { cn } from "@/components/podkit/lib/cn";

export type TableSortOrder = "asc" | "desc";

export type SortableTableHeadProps = {
    columnName: string;
    sortOrder?: TableSortOrder;
    onSort: (sortBy: string, sortOrder: TableSortOrder) => void;
} & HideableCellProps;
export const SortableTableHead = React.forwardRef<
    HTMLTableCellElement,
    React.HTMLAttributes<HTMLTableCellElement> & SortableTableHeadProps
>(({ columnName, sortOrder, children, onSort, ...props }, ref) => {
    const handleClick = useCallback(() => {
        onSort(columnName, sortOrder === "asc" ? "desc" : "asc");
    }, [onSort, columnName, sortOrder]);

    return (
        <TableHead
            ref={ref}
            {...props}
            aria-sort={sortOrder === "asc" ? "ascending" : sortOrder === "desc" ? "descending" : "none"}
        >
            <Button
                variant="ghost"
                onClick={handleClick}
                className="-h-9 flex flex-row items-center gap-1 px-2 py-1 font-bold group"
                aria-label={sortOrder === "asc" ? "Sort ascending" : "Sort descending"}
                data-tracking-id-none
            >
                {children}
                {/* Show chevron on hover for unsorted columns, always show for sorted columns */}
                <span className={cn(!sortOrder && "invisible group-hover:visible")}>
                    {sortOrder === "asc" ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
                </span>
            </Button>
        </TableHead>
    );
});
SortableTableHead.displayName = "SortableTableHead";
